/** SUNTRONIX ONE — ONE TELEPHONY workspace.
 * Analytics · Call Log · Log-a-call · Screen-pop (simulate incoming) · ONE AI. */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Phone, PhoneIncoming, PhoneOutgoing, PhoneMissed, Clock, PhoneCall,
  Sparkles, Search, PlusCircle, X, Settings, RefreshCw,
} from "lucide-react";
import { CallerPopup } from "@/components/app/CallerPopup";

const input = "w-full text-[12px] border border-slate-200 rounded-md px-2.5 py-1.5 focus:ring-2 focus:ring-teal-500/40 outline-none";

const fmtDur = (s) => {
  if (!s && s !== 0) return "—";
  const m = Math.floor(s / 60), r = s % 60;
  return m ? `${m}m ${r}s` : `${r}s`;
};

const Kpi = ({ icon: Icon, label, value, tint, testId }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid={testId}>
    <div className="flex items-center gap-2">
      <div className={`grid h-8 w-8 place-items-center rounded-lg ${tint}`}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
    </div>
    <div className="mt-2 text-2xl font-semibold text-slate-900">{value}</div>
  </div>
);

export default function OneTelephony() {
  const [status, setStatus] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [calls, setCalls] = useState([]);
  const [outcomes, setOutcomes] = useState([]);
  const [q, setQ] = useState("");
  const [caller, setCaller] = useState(null);
  const [simNumber, setSimNumber] = useState("");
  const [logOpen, setLogOpen] = useState(false);
  const [aiCall, setAiCall] = useState(null);
  const [transcript, setTranscript] = useState("");
  const [proposal, setProposal] = useState(null);
  const [actionCall, setActionCall] = useState(null);
  const [actionKind, setActionKind] = useState(null);

  const load = useCallback(() => {
    api.get("/telephony/status").then(({ data }) => setStatus(data)).catch(() => {});
    api.get("/telephony/analytics").then(({ data }) => setAnalytics(data)).catch(() => {});
    api.get("/telephony/outcomes").then(({ data }) => setOutcomes(data.outcomes || [])).catch(() => {});
    api.get(`/telephony/calls${q ? `?q=${encodeURIComponent(q)}` : ""}`)
      .then(({ data }) => setCalls(data.items || [])).catch(() => {});
  }, [q]);
  useEffect(() => { load(); }, [load]);

  const simulate = async () => {
    if (!simNumber.trim()) return;
    try {
      const { data } = await api.get(`/telephony/identify?number=${encodeURIComponent(simNumber)}`);
      setCaller(data);
    } catch (e) { toast.error("Lookup failed"); }
  };

  const setOutcome = async (id, outcome) => {
    try { await api.patch(`/telephony/calls/${id}`, { outcome }); load(); }
    catch { toast.error("Update failed"); }
  };

  const runAi = async () => {
    if (!transcript.trim() || !aiCall) return;
    setProposal({ loading: true });
    try {
      const { data } = await api.post(`/telephony/calls/${aiCall.id}/ai-summary`, { transcript });
      setProposal(data.proposal);
    } catch (e) {
      setProposal(null);
      toast.error(e.response?.data?.detail || "ONE AI failed");
    }
  };

  const applyProposal = async () => {
    if (!aiCall || !proposal) return;
    try {
      const note = `AI summary — ${proposal.discussion}${proposal.commitment ? ` | Commitment: ${proposal.commitment}` : ""}${proposal.next_action ? ` | Next: ${proposal.next_action}` : ""}`;
      await api.post(`/telephony/calls/${aiCall.id}/add-note`, { note });
      if (proposal.follow_up_date && proposal.next_action) {
        await api.post(`/telephony/calls/${aiCall.id}/create-followup`, {
          due_date: proposal.follow_up_date, action: proposal.next_action,
        });
      }
      toast.success("AI summary applied (confirmed by you)");
      setAiCall(null); setTranscript(""); setProposal(null); load();
    } catch { toast.error("Could not apply"); }
  };

  // caller-popup actions -> open small forms
  const onCallerAction = (key, c) => {
    if (key === "ADD_NOTE" || key === "CREATE_TICKET" || key === "CREATE_FOLLOWUP") {
      setActionCall(c); setActionKind(key); setCaller(null);
    } else {
      toast.info(`${key.replace(/_/g, " ")} — open the relevant module to complete.`);
    }
  };

  return (
    <div className="mx-auto max-w-[1200px] space-y-4" data-testid="one-telephony-page">
      <header className="flex flex-wrap items-center gap-3">
        <div className="grid h-10 w-10 place-items-center rounded-lg bg-teal-900 text-white">
          <PhoneCall className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <div className="font-heading text-xl font-semibold">ONE Telephony</div>
          <div className="text-[11px] text-slate-500">
            Caller identification · Click-to-call · Call logging · Analytics
          </div>
        </div>
        {status && (
          <span
            data-testid="telephony-status-badge"
            className={`rounded px-2 py-1 text-[10px] font-semibold ${
              status.status === "READY" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"
            }`}
          >
            {status.status.replace(/_/g, " ")} · {status.provider}
          </span>
        )}
        <a
          href="/admin/one-connect/telephony"
          data-testid="telephony-config-link"
          className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2.5 py-1.5 text-[11px] text-slate-600 hover:bg-slate-50"
        >
          <Settings className="h-3.5 w-3.5" /> Setup
        </a>
      </header>

      {status && status.status !== "READY" && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-[12px] text-amber-800" data-testid="telephony-setup-banner">
          <b>SETUP REQUIRED</b> — no telephony provider configured. Caller ID, click-to-call and manual/webhook
          logging work now; add a provider in <a className="underline" href="/admin/one-connect/telephony">ONE Connect → Telephony</a> to go fully live.
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <Kpi icon={PhoneCall} label="Calls Today" value={analytics?.calls_today ?? 0} tint="bg-teal-100 text-teal-800" testId="kpi-today" />
        <Kpi icon={PhoneIncoming} label="Answered" value={analytics?.answered ?? 0} tint="bg-emerald-100 text-emerald-800" testId="kpi-answered" />
        <Kpi icon={PhoneMissed} label="Missed" value={analytics?.missed ?? 0} tint="bg-rose-100 text-rose-800" testId="kpi-missed" />
        <Kpi icon={Clock} label="Avg Duration" value={fmtDur(analytics?.avg_duration_seconds)} tint="bg-blue-100 text-blue-800" testId="kpi-avg" />
        <Kpi icon={Phone} label="Call Back Due" value={analytics?.call_back_due_count ?? 0} tint="bg-amber-100 text-amber-800" testId="kpi-callback" />
      </div>

      {/* Actions row */}
      <div className="grid gap-3 lg:grid-cols-2">
        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="simulate-card">
          <div className="mb-2 text-[12px] font-semibold">Incoming caller lookup (screen-pop)</div>
          <div className="flex gap-2">
            <input
              value={simNumber}
              onChange={(e) => setSimNumber(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && simulate()}
              placeholder="Enter incoming number e.g. +91 98…"
              className={input + " font-mono"}
              data-testid="sim-number-input"
            />
            <button onClick={simulate} data-testid="sim-lookup-btn"
              className="inline-flex items-center gap-1 rounded-md bg-teal-800 px-3 py-1.5 text-[11px] font-semibold text-white">
              <Search className="h-3.5 w-3.5" /> Identify
            </button>
          </div>
          <div className="mt-1.5 text-[10px] text-slate-400">
            Simulates the pop your provider triggers on a live incoming call.
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="log-card">
          <div className="mb-2 flex items-center justify-between">
            <div className="text-[12px] font-semibold">Log a call</div>
            <button onClick={() => setLogOpen(true)} data-testid="open-log-form"
              className="inline-flex items-center gap-1 rounded-md border border-teal-700 px-2.5 py-1 text-[11px] font-semibold text-teal-800">
              <PlusCircle className="h-3.5 w-3.5" /> New log
            </button>
          </div>
          <div className="text-[11px] text-slate-500">
            Record a call made outside the app, or from a click-to-call, and set its outcome.
          </div>
        </div>
      </div>

      {/* Call log */}
      <div className="rounded-xl border border-slate-200 bg-white" data-testid="call-log-card">
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-3 py-2">
          <div className="text-[12px] font-semibold">Call Log</div>
          <div className="ml-auto flex items-center gap-2">
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search number / name…"
              className="w-48 rounded-md border border-slate-200 px-2 py-1 text-[11px]" data-testid="call-search" />
            <button onClick={load} className="text-slate-500 hover:text-slate-800" data-testid="call-refresh">
              <RefreshCw className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-[11px]">
            <thead className="bg-slate-50 text-left text-[10px] uppercase text-slate-500">
              <tr>
                <th className="px-3 py-2">Dir</th>
                <th className="px-3 py-2">Number</th>
                <th className="px-3 py-2">Contact / Customer</th>
                <th className="px-3 py-2">Agent</th>
                <th className="px-3 py-2">Start</th>
                <th className="px-3 py-2">Dur</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Outcome</th>
                <th className="px-3 py-2">AI</th>
              </tr>
            </thead>
            <tbody>
              {calls.map((c) => (
                <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`call-row-${c.id}`}>
                  <td className="px-3 py-2">
                    {c.direction === "INCOMING"
                      ? <PhoneIncoming className="h-3.5 w-3.5 text-emerald-600" />
                      : <PhoneOutgoing className="h-3.5 w-3.5 text-teal-600" />}
                  </td>
                  <td className="px-3 py-2 font-mono text-[10px]">{c.phone}</td>
                  <td className="px-3 py-2">
                    <div className="text-slate-800">{c.contact_name || "—"}</div>
                    <div className="text-[9px] text-slate-400">{c.customer_name || c.match_type}</div>
                  </td>
                  <td className="px-3 py-2 text-[10px] text-slate-500">{c.agent_email || "system"}</td>
                  <td className="px-3 py-2 text-[10px] text-slate-500">{(c.start_time || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="px-3 py-2">{fmtDur(c.duration_seconds)}</td>
                  <td className="px-3 py-2">
                    <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[9px] font-semibold text-slate-700">{c.status}</span>
                  </td>
                  <td className="px-3 py-2">
                    <select value={c.outcome || ""} onChange={(e) => setOutcome(c.id, e.target.value)}
                      data-testid={`call-outcome-${c.id}`}
                      className="rounded border border-slate-200 px-1 py-0.5 text-[10px]">
                      <option value="">— set —</option>
                      {outcomes.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-2">
                    <button onClick={() => { setAiCall(c); setTranscript(""); setProposal(null); }}
                      data-testid={`call-ai-${c.id}`} className="text-violet-600 hover:text-violet-800" title="ONE AI summary">
                      <Sparkles className="h-3.5 w-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
              {!calls.length && (
                <tr><td colSpan={9} className="px-3 py-8 text-center text-slate-400">No calls logged yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* By user / by customer */}
      <div className="grid gap-3 lg:grid-cols-2">
        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="by-user-card">
          <div className="mb-2 text-[12px] font-semibold">Calls by Agent</div>
          {(analytics?.by_user || []).map((r) => (
            <div key={r.agent} className="flex items-center justify-between border-t border-slate-100 py-1 text-[11px]">
              <span className="truncate text-slate-600">{r.agent}</span>
              <span className="font-semibold">{r.calls}</span>
            </div>
          ))}
          {!(analytics?.by_user || []).length && <div className="text-[11px] text-slate-400">No data yet.</div>}
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="by-customer-card">
          <div className="mb-2 text-[12px] font-semibold">Calls by Customer</div>
          {(analytics?.by_customer || []).map((r) => (
            <div key={r.customer} className="flex items-center justify-between border-t border-slate-100 py-1 text-[11px]">
              <span className="truncate text-slate-600">{r.customer}</span>
              <span className="font-semibold">{r.calls}</span>
            </div>
          ))}
          {!(analytics?.by_customer || []).length && <div className="text-[11px] text-slate-400">No data yet.</div>}
        </div>
      </div>

      {caller && <CallerPopup caller={caller} onClose={() => setCaller(null)} onAction={onCallerAction} />}

      {logOpen && <LogCallDialog outcomes={outcomes} onClose={() => setLogOpen(false)} onSaved={() => { setLogOpen(false); load(); }} />}

      {actionCall && (
        <QuickActionDialog call={actionCall} kind={actionKind}
          onClose={() => { setActionCall(null); setActionKind(null); }}
          onDone={() => { setActionCall(null); setActionKind(null); load(); }} />
      )}

      {aiCall && (
        <div className="fixed inset-0 z-[120] flex items-start justify-center bg-slate-900/40 p-4 pt-16" onClick={() => setAiCall(null)}>
          <div className="w-full max-w-lg rounded-2xl bg-white p-4 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="ai-summary-dialog">
            <div className="mb-2 flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-violet-600" />
              <div className="flex-1 text-[13px] font-semibold">ONE AI — Call Summary</div>
              <button onClick={() => setAiCall(null)}><X className="h-4 w-4 text-slate-400" /></button>
            </div>
            <div className="text-[11px] text-slate-500">Paste the call transcript / notes. AI proposes a summary — nothing is saved until you confirm.</div>
            <textarea value={transcript} onChange={(e) => setTranscript(e.target.value)} rows={5}
              placeholder="Transcript or bullet notes…" className={input + " mt-2"} data-testid="ai-transcript" />
            <div className="mt-2 flex gap-2">
              <button onClick={runAi} data-testid="ai-run"
                className="rounded-md bg-violet-600 px-3 py-1.5 text-[11px] font-semibold text-white">Summarise</button>
            </div>
            {proposal?.loading && <div className="mt-3 text-[11px] text-slate-500">Thinking…</div>}
            {proposal && !proposal.loading && (
              <div className="mt-3 space-y-1.5 rounded-md border border-violet-200 bg-violet-50 p-3 text-[11px]" data-testid="ai-proposal">
                <div><b>Discussion:</b> {proposal.discussion || "—"}</div>
                <div><b>Commitment:</b> {proposal.commitment || "—"}</div>
                <div><b>Next action:</b> {proposal.next_action || "—"}</div>
                <div><b>Follow-up date:</b> {proposal.follow_up_date || "—"}</div>
                <button onClick={applyProposal} data-testid="ai-apply"
                  className="mt-1 rounded-md bg-teal-800 px-3 py-1.5 text-[11px] font-semibold text-white">
                  Confirm &amp; apply
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function LogCallDialog({ outcomes, onClose, onSaved }) {
  const [f, setF] = useState({ direction: "OUTGOING", number: "", contact_name: "",
    customer_name: "", status: "COMPLETED", outcome: "", duration_seconds: "", notes: "" });
  const save = async () => {
    if (!f.number.trim()) return toast.error("Number is required");
    try {
      await api.post("/telephony/calls", {
        ...f,
        duration_seconds: f.duration_seconds ? Number(f.duration_seconds) : null,
        outcome: f.outcome || null,
      });
      toast.success("Call logged"); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="fixed inset-0 z-[120] flex items-start justify-center bg-slate-900/40 p-4 pt-16" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl bg-white p-4 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="log-call-dialog">
        <div className="mb-3 flex items-center justify-between">
          <div className="text-[13px] font-semibold">Log a call</div>
          <button onClick={onClose}><X className="h-4 w-4 text-slate-400" /></button>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <label className="text-[10px] text-slate-500">Direction
            <select value={f.direction} onChange={(e) => setF({ ...f, direction: e.target.value })} className={input} data-testid="log-direction">
              <option value="OUTGOING">Outgoing</option><option value="INCOMING">Incoming</option>
            </select></label>
          <label className="text-[10px] text-slate-500">Number *
            <input value={f.number} onChange={(e) => setF({ ...f, number: e.target.value })} className={input + " font-mono"} data-testid="log-number" /></label>
          <label className="text-[10px] text-slate-500">Contact name
            <input value={f.contact_name} onChange={(e) => setF({ ...f, contact_name: e.target.value })} className={input} /></label>
          <label className="text-[10px] text-slate-500">Customer
            <input value={f.customer_name} onChange={(e) => setF({ ...f, customer_name: e.target.value })} className={input} /></label>
          <label className="text-[10px] text-slate-500">Status
            <select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} className={input} data-testid="log-status">
              {["COMPLETED","CONNECTED","MISSED","NO_ANSWER","BUSY","FAILED"].map((s) => <option key={s}>{s}</option>)}
            </select></label>
          <label className="text-[10px] text-slate-500">Duration (sec)
            <input type="number" value={f.duration_seconds} onChange={(e) => setF({ ...f, duration_seconds: e.target.value })} className={input} /></label>
          <label className="col-span-2 text-[10px] text-slate-500">Outcome
            <select value={f.outcome} onChange={(e) => setF({ ...f, outcome: e.target.value })} className={input} data-testid="log-outcome">
              <option value="">— none —</option>{outcomes.map((o) => <option key={o}>{o}</option>)}
            </select></label>
          <label className="col-span-2 text-[10px] text-slate-500">Notes
            <textarea value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} rows={2} className={input} /></label>
        </div>
        <button onClick={save} data-testid="log-save" className="mt-3 w-full rounded-md bg-teal-800 py-2 text-[12px] font-semibold text-white">Save call</button>
      </div>
    </div>
  );
}

function QuickActionDialog({ call, kind, onClose, onDone }) {
  const [note, setNote] = useState("");
  const [subject, setSubject] = useState("");
  const [desc, setDesc] = useState("");
  const [due, setDue] = useState("");
  const [action, setAction] = useState("");
  const submit = async () => {
    try {
      if (kind === "ADD_NOTE") {
        if (!note.trim()) return;
        await api.post(`/telephony/calls/${call.id}/add-note`, { note });
      } else if (kind === "CREATE_TICKET") {
        if (!subject.trim() || !desc.trim()) return toast.error("Subject & description required");
        await api.post(`/telephony/calls/${call.id}/create-ticket`, { subject, description: desc });
      } else if (kind === "CREATE_FOLLOWUP") {
        if (!due || !action.trim()) return toast.error("Date & action required");
        await api.post(`/telephony/calls/${call.id}/create-followup`, { due_date: due, action });
      }
      toast.success("Done"); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="fixed inset-0 z-[130] flex items-start justify-center bg-slate-900/40 p-4 pt-20" onClick={onClose}>
      <div className="w-full max-w-sm rounded-2xl bg-white p-4 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="quick-action-dialog">
        <div className="mb-2 text-[13px] font-semibold">{kind.replace(/_/g, " ")} · {call.name || call.contact_name || call.phone}</div>
        {kind === "ADD_NOTE" && (
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={4} placeholder="Note…" className={input} data-testid="qa-note" />
        )}
        {kind === "CREATE_TICKET" && (
          <div className="space-y-2">
            <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject" className={input} data-testid="qa-subject" />
            <textarea value={desc} onChange={(e) => setDesc(e.target.value)} rows={3} placeholder="Description" className={input} data-testid="qa-desc" />
          </div>
        )}
        {kind === "CREATE_FOLLOWUP" && (
          <div className="space-y-2">
            <input type="date" value={due} onChange={(e) => setDue(e.target.value)} className={input} data-testid="qa-due" />
            <input value={action} onChange={(e) => setAction(e.target.value)} placeholder="Action" className={input} data-testid="qa-action" />
          </div>
        )}
        <div className="mt-3 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-md border border-slate-200 px-3 py-1.5 text-[11px]">Cancel</button>
          <button onClick={submit} data-testid="qa-submit" className="rounded-md bg-teal-800 px-3 py-1.5 text-[11px] font-semibold text-white">Save</button>
        </div>
      </div>
    </div>
  );
}
